import { Capacitor } from "@capacitor/core";
import { EnvironmentBadgedLogo } from "@client/components/EnvironmentBadgedLogo";
import { useEffectiveUser } from "@client/hooks/use-effective-user";
import { opsBasePath } from "@client/ops/email/opsTabs";
import { specGrantsBasePath } from "@lib/specGrants/requestHref";
import { type AppBarApp } from "@sfc/ui";
import {
  GitMergeIcon,
  InboxIcon,
  type LucideIcon,
  ZapIcon,
} from "lucide-react";

export type AppDef = {
  key: "s-process" | "spec-grants" | "ops";
  label: string;
  href: string;
  icon: LucideIcon;
  logo: React.ElementType<{ className?: string }>;
  requiresSfcAdmin: boolean;
};

export const sProcessApp = defineApp({
  key: "s-process",
  label: "S-Process",
  href: "/",
  icon: GitMergeIcon,
  requiresSfcAdmin: false,
});

export const specGrantsApp = defineApp({
  key: "spec-grants",
  label: "Spec Grants",
  href: specGrantsBasePath,
  icon: ZapIcon,
  requiresSfcAdmin: true,
});

export const opsApp = defineApp({
  key: "ops",
  label: "Ops",
  href: opsBasePath,
  icon: InboxIcon,
  requiresSfcAdmin: true,
});

const apps: AppDef[] = [sProcessApp, specGrantsApp, opsApp];

export function useSwitchableApps(currentApp: AppDef): AppBarApp[] {
  const { data: effectiveUser } = useEffectiveUser();
  const isSfcAdmin = effectiveUser?.isSfcAdmin === true;

  return apps
    .filter((app) => (isSfcAdmin || !app.requiresSfcAdmin) && isAppBundled(app))
    .map((app) => toAppBarApp(app, currentApp));
}

function defineApp(app: Omit<AppDef, "logo">): AppDef {
  return { ...app, logo: buildAppLogo(app.icon) };
}

function buildAppLogo(icon: LucideIcon) {
  return function AppLogo({ className }: { className?: string }) {
    return <EnvironmentBadgedLogo icon={icon} className={className} />;
  };
}

// The native iPad shell bundles only the S-Process entry.
function isAppBundled(app: AppDef) {
  return app === sProcessApp || !Capacitor.isNativePlatform();
}

// Each app ships as its own HTML entry, so moving to another app has to be a
// full document load rather than a client-side route change.
function toAppBarApp(app: AppDef, currentApp: AppDef): AppBarApp {
  const { key, label, href, icon } = app;

  if (app !== currentApp) {
    return { key, label, href, icon, reloadDocument: true };
  }

  // `~` resolves the href from the document root rather than the app's router
  // base (Spec Grants and Ops nest their routes under their own path).
  return { key, label, href: `~${href}`, icon };
}
